import { sortBy } from "lodash-es";
import { useMemo } from "react";

import {
  getGroupIdFromAccessId,
  getIdFromAccessId,
  getStudentIdFromAccessId,
  groupAccessIdFromGroupId,
  isGroupAccessId,
  isStudentAccessId,
  studentAccessIdFromUserId,
} from "../../../shared/access-id";
import { useIsAssessing } from "../section/assessment/hooks";

import { indexToLetters } from "./helpers";

import type { SectionType, PageGroupType, PageType, SubmissionType } from "components/server-types";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import {
  ORDER_BY_CREATED,
  ORDER_BY_CREATED_DATE_DESC,
  useCustomOrdered,
} from "mds/hooks/use-ordered";
import { useWorksheetAccessIds } from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  FullGroupSubmissionType,
  FullIndividualSubmissionType,
  UserWithSectionUserType,
  selectCurrentCourse,
  selectFullSubmissionsByPageId,
  selectPageGroupsByPageId,
  selectUsersInCurrentCourse,
} from "store/selectors";
import { selectPageGroupUsersByPageId } from "store/selectors/page-groups";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import type { ExtendedPageGroupUserType } from "store/selectors/types";
import { useListSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";
import { getUserShortName } from "utils/user-utils";

export type ResponseType = {
  title: string;
  id: string;
};

/**
 * Anonymize responses by shuffling them with a deterministic seed (pageId).
 * Using Fisher-Yates shuffle algorithm.
 */

export const anonymizeResponses = (
  page: PageType,
  responses: ResponseType[],
  interactionMode: InteractionMode,
): ResponseType[] => {
  const seededRandom = (seed: number) => {
    // Deterministic pseudo-random number generator
    const x = Math.sin(seed) * 10000;
    // Number returned is between 0 and 1
    return x - Math.floor(x);
  };
  // Create a seed by summing the char codes of the pageId
  let pageSeed = page.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);

  // We pre-sort by ID in case the responses aren't. Sometimes "order" is "0" for class responses
  // and we want to ensure that the order is consistent going into the anonymization function.
  const shuffledResponses = sortBy(responses, "id").slice();
  for (let i = shuffledResponses.length - 1; i > 0; i -= 1) {
    // Generate a pseudo-random index based on the seed
    const j = Math.floor(seededRandom(pageSeed) * (i + 1));
    // Swap elements at index i and j
    [shuffledResponses[i], shuffledResponses[j]] = [shuffledResponses[j], shuffledResponses[i]];
    pageSeed += 1; // Increment the seed to change the random number sequence
  }

  if (interactionMode === InteractionMode.INDIVIDUAL) {
    //   Rename to Student 1, Student 2, etc.
    return shuffledResponses.map((response, index) => ({
      title: t("common.student_title", { number: index + 1 }),
      id: response.id,
    }));
  }
  // Rename to Anon. Group 1, Anon. Group 2, etc.
  return shuffledResponses.map((response, index) => ({
    title: t("common.group_title_anonymous", { letter: indexToLetters(index) }),
    id: response.id,
  }));
};

// Note that that for groups pages, we handle differently depending on assessment mode:
// Outside of assessment, we show all the groups (even empty ones).
// This is because the instructor can see all groups in the group configuration tab and if group membership has been changed,
// the empty groups can contain the old student data.
// However, when assessing, we only want to show the groups that have submissions, because
// there is no purpose to assess any work not tied to a student
// (and it's likely a blank submission from an empty group in standard use, given we
// commonly have 1-2 extra groups for students self-assign groups).
export const getResponses = (
  anonymize: boolean,
  isAssessing: boolean,
  page: PageType,
  interactionMode: InteractionMode,
  pageGroups: PageGroupType[],
  courseUsers: UserWithSectionUserType[],
  worksheetAccessIds: string[],
  enabledPageGroupUsers?: ExtendedPageGroupUserType[],
): ResponseType[] => {
  // Class pages don't feature a responses tab
  if (interactionMode === InteractionMode.SHARED_CLASS) {
    return [];
  }

  let responses = [] as ResponseType[];

  if (interactionMode === InteractionMode.INDIVIDUAL) {
    // Start by populating the options for all the known users according to CourseUser data.
    const knownUsers = courseUsers.filter((user) => user.role === "student");
    const knownUserResponses = knownUsers.map((user) => ({
      title: getUserShortName(user),
      id: studentAccessIdFromUserId(user.id),
    }));
    responses = responses.concat(knownUserResponses);

    // Potentially extend that set of options with additional users gleaned from the worksheet. This takes care
    // of the case of a Page created by means of uploading a JSON snapshot, which will tend to have a worksheet
    // that makes reference to Users that don't actually exist in the current environment.
    if (page?.is_imported && worksheetAccessIds.length > 0) {
      const knownUserIds = new Set(knownUsers.map((user) => user.id));
      const accessIdsFromWorksheet = worksheetAccessIds.filter(isStudentAccessId);
      const userIdsFromWorksheet = accessIdsFromWorksheet.map(getStudentIdFromAccessId);
      const unknownUserIds = userIdsFromWorksheet.filter((id) => !knownUserIds.has(id));
      const unknownUserResponses = unknownUserIds.map((userId) => ({
        title: `User ${userId}`,
        id: studentAccessIdFromUserId(userId),
      }));
      responses = responses.concat(unknownUserResponses);
    }
  } else {
    // TODO: this logic somewhat duplicates the logic in getTotalSubmissions in assessment/helpers.ts
    // Extract into a helper function.
    // Note that unlike `getTotalSubmissions`, when not assessing
    // we want to show all groups (even empty ones).
    const visibleGroupIds =
      isAssessing && enabledPageGroupUsers
        ? new Set(enabledPageGroupUsers.map((pgu) => pgu.page_group_id))
        : new Set(pageGroups.map((pg) => pg.id)); // Show all groups when not assessing

    // Start by populating the options for visibleGroupIds according to PageGroup data.
    // Preserve original group numbers by using the original index from pageGroups.
    const knownGroupResponses = pageGroups
      .map((group, index) => ({ group, originalIndex: index }))
      .filter(({ group }) => visibleGroupIds.has(group.id))
      .map(({ group, originalIndex }) => ({
        title: t("common.group_title", { number: originalIndex + 1 }),
        id: groupAccessIdFromGroupId(group.id),
      }));
    responses = responses.concat(knownGroupResponses);

    // Potentially extend options with additional groups gleaned from the worksheet, similar to above.
    if (page?.is_imported && worksheetAccessIds.length > 0) {
      const knownGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
      const accessIdsFromWorksheet = worksheetAccessIds.filter(isGroupAccessId);
      const groupIdsFromWorksheet = accessIdsFromWorksheet.map(getGroupIdFromAccessId);
      const unknownGroupIds = groupIdsFromWorksheet.filter((id) => !knownGroupIds.has(id));
      const unknownGroupResponses = unknownGroupIds.map((groupId) => ({
        title: `Group ${groupId}`,
        id: groupAccessIdFromGroupId(groupId),
      }));
      responses = responses.concat(unknownGroupResponses);
    }
  }

  // Optional anonymization.
  if (anonymize && page) {
    responses = anonymizeResponses(page, responses, interactionMode);
  } else {
    // Ensure consistent ordering of responses when not anonymizing.
    responses = sortBy(responses, "title").slice();
  }

  // Offer the default "class responses" option first, unless we're in the middle of an assessment.
  // Do this after anonymization, so that the "class responses" option is always the first one.
  if (!isAssessing) {
    responses = [
      {
        title: t("presentation.view_options.responses"),
        id: "",
      },
      ...responses,
    ];
  }

  return responses;
};

const filterSubmissionsByResponse = (
  submission: FullIndividualSubmissionType | FullGroupSubmissionType,
  response: ResponseType,
) => {
  const id = getIdFromAccessId(response.id);
  if (!id) return false;

  if (isIndividualSubmission(submission)) {
    return submission.course_user.user_id === id;
  }
  return submission.page_group_id === id;
};

/**
 * This function sorts an array of responses based on the following logic:
 * - Responses whose most recent submission is assessed come first.
 * - Responses with submissions (but none assessed) come second.
 * - Responses with assessed submissions come third.
 * - Responses with no submissions come last.
 * - Among responses with assessed submissions, non-official submissions come before official ones.
 */
export const compareResponsesForAssessmentMode =
  (submissions: FullIndividualSubmissionType[] | FullGroupSubmissionType[]) =>
  (a: ResponseType, b: ResponseType) => {
    // Find the group or student submission based on the access id in the first response.
    const aSubmissions = submissions.filter((submission) =>
      filterSubmissionsByResponse(submission, a),
    ) as FullIndividualSubmissionType[] | FullGroupSubmissionType[];

    // Find the group or student submission based on the access id in the second response.
    const bSubmissions = submissions.filter((submission) =>
      filterSubmissionsByResponse(submission, b),
    ) as FullIndividualSubmissionType[] | FullGroupSubmissionType[];

    // Helper function to determine the tier for a set of submissions
    const getTier = (
      submissionList: FullIndividualSubmissionType[] | FullGroupSubmissionType[],
    ) => {
      // No submissions
      if (submissionList.length === 0) {
        return 4;
      }

      // Last submission is the official submission we want first
      if (submissionList[0].official_at) {
        return 1;
      }

      // Some assessed submissions
      const hasAssessed = submissionList.some(
        (submission: SubmissionType) => submission.official_at,
      );
      if (hasAssessed) return 3;

      // Else, some unassessed submissions
      return 2;
    };

    const aTier = getTier(aSubmissions);
    const bTier = getTier(bSubmissions);

    // Sort by tier (lower tier comes first)
    return aTier - bTier;
  };

export const useResponsesForPage = (page: PageType | undefined, isFeaturing = false) => {
  const interactionMode = page?.interaction_mode;
  const assessmentsPublished = !!page?.assessments_published_at;
  const course = useAppSelector(selectCurrentCourse) as SectionType;
  const isAssessing = useIsAssessing();
  const submissions = useAppSelector((s) => selectFullSubmissionsByPageId(s, page?.id));
  const { canParticipateInCourse } = useCapabilities();
  // TODO: add value here to allow instructor to anonymize a course
  const anonymize =
    (isAssessing && !assessmentsPublished && course?.anonymous_grading_enabled) ||
    (page?.anonymize_student_responses && isFeaturing) ||
    (canParticipateInCourse && !isFeaturing);
  const courseUsers = useListSelector(selectUsersInCurrentCourse);
  const pageGroups = useCustomOrdered(
    useAppSelector((s) => selectPageGroupsByPageId(s, page?.id)),
    ORDER_BY_CREATED,
  );
  // Only fetch pageGroupUsers for group pages (not needed for individual pages)
  const pageGroupUsers = useListSelector((s) =>
    page && isGroupPage(page) ? selectPageGroupUsersByPageId(s, page.id) : [],
  );

  const worksheetAccessIds = useWorksheetAccessIds(page?.worksheet_id);
  const responses = useMemo(
    () =>
      getResponses(
        anonymize,
        isAssessing,
        page,
        interactionMode,
        pageGroups,
        courseUsers,
        worksheetAccessIds,
        pageGroupUsers,
      ),
    [
      anonymize,
      isAssessing,
      page,
      interactionMode,
      pageGroups,
      courseUsers,
      worksheetAccessIds,
      pageGroupUsers,
    ],
  );

  // When loading the page while in assessment mode, we need to wait for submissions
  // to be loaded before we do the initial sort, otherwise we get stuck in weirdly ordered state.
  const submissionsLoaded = submissions.length > 0;

  const sortedResponses = useMemo(() => {
    if (isAssessing) {
      return responses.toSorted(
        // We sort submissions by created date in descending order so that the most recent submission is first.
        compareResponsesForAssessmentMode(submissions.toSorted(ORDER_BY_CREATED_DATE_DESC)),
      );
    }

    return responses;
    // Do not resort when submissions change. We do not want the order of responses
    // to change when a new assessment is created while in assessment mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessing, submissionsLoaded, anonymize]);

  // If we are not assessing, we don't want the cached version otherwise with anonymize toggle
  // will not work properly.
  return { responses: isAssessing ? sortedResponses : responses, anonymize };
};
